import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Save, Search, Link as LinkIcon, Ban, CheckCircle2, ArrowRightCircle, Send, Settings, StickyNote, Lock, RefreshCw, RotateCcw, Merge } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import CurrencyInput from '../../components/Admin/CurrencyInput';
import I18nTripleFields, { I18nTriple } from '../../components/Admin/I18nTripleFields';
import { emptyI18nTriple } from '../../i18n/localizeContent';
import {
  MAX_PARCELAS,
  MIN_PARCELAS,
  OpcaoCalculada,
  OpcoesPagamento,
  calcularOpcoes,
  emptyOpcoesPagamento,
  formatMoeda,
  Moeda,
  Cambio,
  labelCambio,
  calcularTotais,
  AbatimentoManual,
  labelOpcaoAdmin,
  temOpcoes
} from '../../utils/orcamentoPagamento';
import { ActionButton, Button, IconButton, Tabs } from '../../components/UI';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

interface ItemLocaleBlock {
  descricao?: string;
}

interface ItemI18n {
  pt: ItemLocaleBlock;
  en: ItemLocaleBlock;
  es: ItemLocaleBlock;
}

interface OrcamentoItem {
  descricao: string;
  i18n?: ItemI18n;
  servicoId?: string | null;
  valor: number;
  quantidade: number;
  abatidoAoFecharOutros?: boolean;
}

interface ServicoOption {
  _id: string;
  nome: string;
  i18n?: I18nTriple;
  valorPadrao: number;
  abatidoAoFecharOutros?: boolean;
}

type OrcamentoStatus = 'draft' | 'sent' | 'accepted' | 'converted' | 'expired' | 'revoked' | 'declined';
type MotivoRecusa = 'preco' | 'condicoes_pagamento' | 'outro_fornecedor' | 'adiou' | 'desistiu' | 'outro';

interface RecusaOrcamento {
  motivo: MotivoRecusa;
  comentario?: string;
  recusadoAt?: string;
}
type FormaPagamento = 'avista' | 'parcelado_2' | 'parcelado_3' | 'personalizado';
type OrigemCliente =
  | 'indicacao'
  | 'instagram'
  | 'facebook'
  | 'tiktok'
  | 'youtube'
  | 'google'
  | 'landing_page'
  | 'site'
  | 'whatsapp'
  | 'evento'
  | 'cliente_antigo'
  | 'outro';


interface OrcamentoData {
  _id?: string;
  nomeCliente: string;
  emailCliente?: string;
  telefoneCliente?: string;
  origemCliente?: OrigemCliente | '';
  origemDetalhe?: string;
  notasInternas?: string;
  itens: OrcamentoItem[];
  moeda?: Moeda;
  cambio?: Cambio | null;
  formaPagamento: FormaPagamento;
  opcoesPagamento?: OpcoesPagamento;
  abatimentoManual?: AbatimentoManual;
  opcoesCalculadas?: OpcaoCalculada[];
  opcaoEscolhida?: OpcaoCalculada | null;
  corpoTexto: string;
  i18n?: I18nTriple;
  idioma?: 'pt' | 'en' | 'es';
  status?: OrcamentoStatus;
  recusa?: RecusaOrcamento | null;
  total?: number;
  createdAt?: string;
  viewUrl?: string;
  duplicadoDe?: string | null;
  mescladoDe?: string[];
  mescladoEm?: string | null;
}

const FORMAS_PAGAMENTO: { value: FormaPagamento; label: string }[] = [
  { value: 'avista', label: 'À vista' },
  { value: 'parcelado_2', label: 'Parcelado em 2 vezes' },
  { value: 'parcelado_3', label: 'Parcelado em 3 vezes' },
  { value: 'personalizado', label: 'Personalizado' }
];

const labelFormaPagamento = (forma?: string) =>
  FORMAS_PAGAMENTO.find((f) => f.value === forma)?.label || forma || '—';

const STATUS_LABEL: Record<OrcamentoStatus, { label: string; className: string }> = {
  draft: { label: 'Rascunho', className: 'bg-gray-100 text-gray-600' },
  sent: { label: 'Aguardando cliente', className: 'bg-amber-100 text-amber-700' },
  accepted: { label: 'Aceito', className: 'bg-green-100 text-green-700' },
  converted: { label: 'Convertido em contrato', className: 'bg-blue-100 text-blue-700' },
  expired: { label: 'Link expirado', className: 'bg-gray-100 text-gray-600' },
  revoked: { label: 'Link revogado', className: 'bg-gray-100 text-gray-600' },
  declined: { label: 'Recusado', className: 'bg-red-100 text-red-700' }
};

// Espelho de MOTIVOS_RECUSA em backend/models/Orcamento.js
const MOTIVO_RECUSA_LABEL: Record<MotivoRecusa, string> = {
  preco: 'Valor acima do esperado',
  condicoes_pagamento: 'Condições de pagamento',
  outro_fornecedor: 'Fechou com outro escritório/profissional',
  adiou: 'Vai deixar para mais tarde',
  desistiu: 'Desistiu do processo',
  outro: 'Outro motivo'
};

// Espelho de ORIGENS_CLIENTE em backend/models/Orcamento.js (e ORIGEM_CLIENTE_LABEL em routes/orcamento.js)
const ORIGEM_CLIENTE_LABEL: Record<OrigemCliente, string> = {
  indicacao: 'Indicação',
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  google: 'Google',
  landing_page: 'Landing page',
  site: 'Site',
  whatsapp: 'WhatsApp',
  evento: 'Evento/palestra',
  cliente_antigo: 'Cliente antigo',
  outro: 'Outro'
};

const ORIGEM_DETALHE_PLACEHOLDER: Partial<Record<OrigemCliente, string>> = {
  indicacao: 'Quem indicou?',
  landing_page: 'Qual landing page / campanha?',
  evento: 'Qual evento?',
  outro: 'Descreva a origem'
};

const labelOrigem = (o: Pick<OrcamentoData, 'origemCliente' | 'origemDetalhe'>) =>
  [o.origemCliente ? ORIGEM_CLIENTE_LABEL[o.origemCliente] : '', o.origemDetalhe].filter(Boolean).join(' — ');

// Mesclagem: só rascunhos/enviados/recusados que ainda não viraram parte de outro orçamento
const podeMesclar = (o: OrcamentoData) => !['accepted', 'converted'].includes(o.status || 'draft') && !o.mescladoEm;

const temInfoInterna = (o: OrcamentoData) => !!(o.origemCliente || o.origemDetalhe || o.notasInternas);

interface InternoForm {
  origemCliente: OrigemCliente | '';
  origemDetalhe: string;
  notasInternas: string;
}

// Origem + notas internas: usado no formulário do orçamento e no modal de edição rápida da lista
const CamposInternos: React.FC<{ value: InternoForm; onChange: (v: InternoForm) => void }> = ({ value, onChange }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">De onde veio o cliente</label>
        <select
          value={value.origemCliente}
          onChange={(e) => onChange({ ...value, origemCliente: e.target.value as OrigemCliente | '' })}
          className="field field--select"
        >
          <option value="">Não informado</option>
          {(Object.keys(ORIGEM_CLIENTE_LABEL) as OrigemCliente[]).map((k) => (
            <option key={k} value={k}>
              {ORIGEM_CLIENTE_LABEL[k]}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Detalhe da origem</label>
        <input
          value={value.origemDetalhe}
          maxLength={200}
          placeholder={(value.origemCliente && ORIGEM_DETALHE_PLACEHOLDER[value.origemCliente]) || 'Opcional'}
          onChange={(e) => onChange({ ...value, origemDetalhe: e.target.value })}
          className="field"
        />
      </div>
    </div>
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">Notas internas</label>
      <textarea
        value={value.notasInternas}
        maxLength={2000}
        rows={3}
        placeholder="Contexto da negociação, perfil do cliente, próximos passos..."
        onChange={(e) => onChange({ ...value, notasInternas: e.target.value })}
        className="field"
      />
      <p className="text-xs text-gray-500 text-right">{value.notasInternas.length}/2000</p>
    </div>
  </div>
);

const emptyItem = (): OrcamentoItem => ({ descricao: '', valor: 0, quantidade: 1 });

const emptyForm = (): OrcamentoData => ({
  nomeCliente: '',
  emailCliente: '',
  telefoneCliente: '',
  origemCliente: '',
  origemDetalhe: '',
  notasInternas: '',
  itens: [emptyItem()],
  moeda: 'EUR',
  cambio: null,
  formaPagamento: 'parcelado_3',
  opcoesPagamento: emptyOpcoesPagamento(),
  abatimentoManual: { valor: 0, descricao: '' },
  corpoTexto: '',
  idioma: 'pt'
});

// Orçamentos antigos só têm `formaPagamento`: converte para as opções equivalentes ao editar.
const opcoesFromLegado = (forma: FormaPagamento): OpcoesPagamento => {
  const opcoes = emptyOpcoesPagamento();
  if (forma === 'parcelado_2' || forma === 'parcelado_3') {
    opcoes.avista.habilitado = false;
    opcoes.parcelados = [{ parcelas: forma === 'parcelado_2' ? 2 : 3, juros: 0 }];
  }
  return opcoes;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

// Serviços são cadastrados em €; num orçamento em BRL o label mostra também o valor convertido
const servicoOptionLabel = (s: ServicoOption, taxaBrl?: number | null) =>
  `${s.i18n?.pt?.nome || s.nome} (€${s.valorPadrao.toFixed(2)}${
    taxaBrl ? ` ≈ ${formatMoeda(round2(s.valorPadrao * taxaBrl), 'BRL')}` : ''
  })`;

const computeTotal = (itens: OrcamentoItem[], abatimentoManual?: AbatimentoManual) =>
  calcularTotais(itens, abatimentoManual).total;

const AdminOrcamento: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const notifications = useNotifications();
  const confirm = useConfirm();

  const [orcamentos, setOrcamentos] = useState<OrcamentoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrcamentoStatus | ''>('');
  const [origemFilter, setOrigemFilter] = useState<OrigemCliente | ''>('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<OrcamentoData>(emptyForm());
  const [saving, setSaving] = useState(false);

  const [i18n, setI18n] = useState<I18nTriple>(emptyI18nTriple());
  const [servicos, setServicos] = useState<ServicoOption[]>([]);
  const [servicoQuery, setServicoQuery] = useState('');

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkOrcamentoId, setLinkOrcamentoId] = useState<string | null>(null);
  const [expiresInDays, setExpiresInDays] = useState('30');
  const [sendingLink, setSendingLink] = useState(false);
  const [viewUrlPreview, setViewUrlPreview] = useState('');
  const [converting, setConverting] = useState<string | null>(null);

  const [aceiteOrcamento, setAceiteOrcamento] = useState<OrcamentoData | null>(null);
  const [aceiteOpcaoId, setAceiteOpcaoId] = useState('');
  const [marcandoAceito, setMarcandoAceito] = useState(false);

  const [internoOrcamento, setInternoOrcamento] = useState<OrcamentoData | null>(null);
  const [internoForm, setInternoForm] = useState<InternoForm>({ origemCliente: '', origemDetalhe: '', notasInternas: '' });
  const [salvandoInterno, setSalvandoInterno] = useState(false);
  const [carregandoCambio, setCarregandoCambio] = useState(false);

  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [mesclando, setMesclando] = useState(false);
  const [duplicando, setDuplicando] = useState<string | null>(null);

  useEffect(() => {
    fetchOrcamentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, origemFilter]);

  useEffect(() => {
    fetchServicos();
  }, []);

  // Vindo de "Orçamento" nos leads do quiz: abre um novo orçamento já com os dados do lead.
  // Limpa o state em seguida para o formulário não reabrir ao recarregar ou voltar.
  useEffect(() => {
    const prefill = (location.state as { prefill?: Partial<OrcamentoData> } | null)?.prefill;
    if (!prefill) return;
    setEditingId(null);
    setFormData({ ...emptyForm(), ...prefill });
    setI18n(emptyI18nTriple());
    setShowForm(true);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchServicos = async () => {
    try {
      const res = await axios.get('/servico?ativo=true');
      setServicos(res.data);
    } catch (error) {
      console.error('Erro ao carregar catálogo de serviços:', error);
    }
  };

  const fetchOrcamentos = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '50' });
      if (searchTerm) params.set('search', searchTerm);
      if (statusFilter) params.set('status', statusFilter);
      if (origemFilter) params.set('origem', origemFilter);
      const res = await axios.get(`/orcamento?${params}`);
      setOrcamentos(res.data.orcamentos);
    } catch (error) {
      console.error(error);
      notifications.error.settingsUpdate('Erro ao carregar orçamentos');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm());
    setI18n(emptyI18nTriple());
    setShowForm(true);
  };

  const openEdit = (o: OrcamentoData) => {
    setEditingId(o._id || null);
    setFormData({
      ...o,
      emailCliente: o.emailCliente || '',
      telefoneCliente: o.telefoneCliente || '',
      origemCliente: o.origemCliente || '',
      origemDetalhe: o.origemDetalhe || '',
      notasInternas: o.notasInternas || '',
      itens: o.itens.length > 0 ? o.itens : [emptyItem()],
      moeda: o.moeda || 'EUR',
      cambio: o.cambio || null,
      abatimentoManual: {
        valor: o.abatimentoManual?.valor || 0,
        descricao: o.abatimentoManual?.descricao || ''
      },
      opcoesPagamento: temOpcoes(o.opcoesPagamento)
        ? {
            avista: { habilitado: !!o.opcoesPagamento?.avista?.habilitado },
            avistaDesconto: {
              habilitado: !!o.opcoesPagamento?.avistaDesconto?.habilitado,
              percentual: o.opcoesPagamento?.avistaDesconto?.percentual || 0
            },
            parcelados: o.opcoesPagamento?.parcelados || []
          }
        : opcoesFromLegado(o.formaPagamento)
    });
    setI18n({
      pt: { corpoTexto: o.i18n?.pt?.corpoTexto || o.corpoTexto || '' },
      en: { corpoTexto: o.i18n?.en?.corpoTexto || '' },
      es: { corpoTexto: o.i18n?.es?.corpoTexto || '' }
    });
    setShowForm(true);
  };

  const handleItemAbatidoChange = (index: number, abatidoAoFecharOutros: boolean) => {
    setFormData((prev) => ({
      ...prev,
      itens: prev.itens.map((item, i) => (i === index ? { ...item, abatidoAoFecharOutros } : item))
    }));
  };

  const handleItemChange = (index: number, field: keyof OrcamentoItem, value: string) => {
    setFormData((prev) => ({
      ...prev,
      itens: prev.itens.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: field === 'descricao' ? value : Number(value) || 0
            }
          : item
      )
    }));
  };

  const moedaForm: Moeda = formData.moeda || 'EUR';
  const taxaBrl = moedaForm === 'BRL' ? formData.cambio?.taxa || null : null;
  const fmt = (n: number) => formatMoeda(n, moedaForm);
  const simboloForm = moedaForm === 'BRL' ? 'R$' : '€';

  const fetchCambio = async (forcar = false): Promise<Cambio | null> => {
    try {
      setCarregandoCambio(true);
      const res = await axios.get(`/cambio?from=EUR&to=BRL${forcar ? '&atualizar=1' : ''}`);
      const { taxa, fonte, obtidaEm, desatualizada } = res.data;
      return { taxa, fonte, obtidaEm, desatualizada };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Não foi possível obter a cotação EUR/BRL');
      return null;
    } finally {
      setCarregandoCambio(false);
    }
  };

  // Converte itens e abatimento pela cotação, para "500" em € não virar R$ 500 (e vice-versa)
  const converterValores = (prev: OrcamentoData, fator: number): Partial<OrcamentoData> => ({
    itens: prev.itens.map((item) => ({ ...item, valor: round2(item.valor * fator) })),
    abatimentoManual: {
      descricao: prev.abatimentoManual?.descricao || '',
      valor: round2((prev.abatimentoManual?.valor || 0) * fator)
    }
  });

  const handleMoedaChange = async (nova: Moeda) => {
    if (nova === moedaForm) return;
    const temValores = formData.itens.some((i) => i.valor > 0) || (formData.abatimentoManual?.valor || 0) > 0;
    const cambio = nova === 'BRL' ? await fetchCambio() : formData.cambio;
    if (nova === 'BRL' && !cambio) return;
    let converter = false;
    if (temValores && cambio?.taxa) {
      converter = await confirm({
        title: 'Converter valores',
        message: `Converter os valores já preenchidos para ${nova === 'BRL' ? 'reais' : 'euros'} pela cotação ${labelCambio(cambio)}? Se não, os números ficam como estão.`,
        confirmText: 'Converter',
        cancelText: 'Manter números'
      });
    }
    setFormData((prev) => ({
      ...prev,
      ...(converter && cambio ? converterValores(prev, nova === 'BRL' ? cambio.taxa : 1 / cambio.taxa) : {}),
      moeda: nova,
      cambio: nova === 'BRL' ? cambio : null
    }));
  };

  // Atualiza a cotação gravada; os valores já digitados não mudam (podem ter sido arredondados à mão)
  const atualizarCambio = async () => {
    const cambio = await fetchCambio(true);
    if (cambio) setFormData((prev) => ({ ...prev, cambio }));
  };

  const addItem = () => {
    setFormData((prev) => ({ ...prev, itens: [...prev.itens, emptyItem()] }));
  };

  const addItemFromServico = (servicoId: string) => {
    const servico = servicos.find((s) => s._id === servicoId);
    if (!servico) return;
    const ptNome = servico.i18n?.pt?.nome || servico.nome;
    setFormData((prev) => ({
      ...prev,
      itens: [
        ...prev.itens,
        {
          descricao: ptNome,
          i18n: {
            pt: { descricao: ptNome },
            en: { descricao: servico.i18n?.en?.nome || '' },
            es: { descricao: servico.i18n?.es?.nome || '' }
          },
          servicoId: servico._id,
          valor: taxaBrl ? round2(servico.valorPadrao * taxaBrl) : servico.valorPadrao,
          quantidade: 1,
          abatidoAoFecharOutros: !!servico.abatidoAoFecharOutros
        }
      ]
    }));
  };

  const removeItem = (index: number) => {
    setFormData((prev) => ({ ...prev, itens: prev.itens.filter((_, i) => i !== index) }));
  };

  const opcoes = formData.opcoesPagamento || emptyOpcoesPagamento();

  const updateOpcoes = (fn: (o: OpcoesPagamento) => OpcoesPagamento) => {
    setFormData((prev) => ({ ...prev, opcoesPagamento: fn(prev.opcoesPagamento || emptyOpcoesPagamento()) }));
  };

  const addParcelado = () => {
    updateOpcoes((o) => {
      const usados = new Set(o.parcelados.map((p) => p.parcelas));
      let n = MIN_PARCELAS;
      while (usados.has(n) && n < MAX_PARCELAS) n += 1;
      return { ...o, parcelados: [...o.parcelados, { parcelas: n, juros: 0 }] };
    });
  };

  const updateParcelado = (index: number, field: 'parcelas' | 'juros', value: string) => {
    updateOpcoes((o) => ({
      ...o,
      parcelados: o.parcelados.map((p, i) =>
        i === index ? { ...p, [field]: field === 'parcelas' ? parseInt(value, 10) || 0 : Number(value) || 0 } : p
      )
    }));
  };

  const removeParcelado = (index: number) => {
    updateOpcoes((o) => ({ ...o, parcelados: o.parcelados.filter((_, i) => i !== index) }));
  };

  const validarOpcoes = (o: OpcoesPagamento): string | null => {
    if (!temOpcoes(o)) return 'Habilite ao menos uma opção de pagamento para o cliente.';
    if (o.avistaDesconto.habilitado && (o.avistaDesconto.percentual <= 0 || o.avistaDesconto.percentual > 100)) {
      return 'Informe um desconto à vista entre 0 e 100%.';
    }
    const vistos = new Set<number>();
    for (const p of o.parcelados) {
      if (!Number.isInteger(p.parcelas) || p.parcelas < MIN_PARCELAS || p.parcelas > MAX_PARCELAS) {
        return `O número de parcelas deve estar entre ${MIN_PARCELAS} e ${MAX_PARCELAS}.`;
      }
      if (p.juros < 0 || p.juros > 100) return 'Os juros do parcelado devem estar entre 0 e 100%.';
      if (vistos.has(p.parcelas)) return `Há mais de uma opção em ${p.parcelas}x — deixe apenas uma.`;
      vistos.add(p.parcelas);
    }
    return null;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const erroOpcoes = validarOpcoes(opcoes);
    if (erroOpcoes) {
      notifications.error.settingsUpdate(erroOpcoes);
      return;
    }
    try {
      setSaving(true);
      const payload = { ...formData, corpoTexto: i18n.pt?.corpoTexto || '', i18n };
      if (editingId) {
        await axios.put(`/orcamento/${editingId}`, payload);
      } else {
        await axios.post('/orcamento', payload);
      }
      notifications.success.settingsUpdated();
      setShowForm(false);
      fetchOrcamentos();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: 'Excluir orçamento', message: 'Este orçamento será excluído permanentemente.', confirmText: 'Excluir', variant: 'danger' }))) return;
    try {
      await axios.delete(`/orcamento/${id}`);
      notifications.success.settingsUpdated();
      fetchOrcamentos();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao excluir');
    }
  };

  const handleDuplicar = async (o: OrcamentoData) => {
    if (!o._id) return;
    try {
      setDuplicando(o._id);
      const res = await axios.post(`/orcamento/${o._id}/duplicar`);
      notifications.success.settingsUpdated();
      fetchOrcamentos();
      openEdit(res.data.orcamento);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao duplicar');
    } finally {
      setDuplicando(null);
    }
  };

  const toggleSelecionado = (id: string) =>
    setSelecionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const orcamentosSelecionados = selecionados
    .map((id) => orcamentos.find((o) => o._id === id))
    .filter((o): o is OrcamentoData => !!o);
  const moedasMistas = new Set(orcamentosSelecionados.map((o) => o.moeda || 'EUR')).size > 1;

  const handleMesclar = async () => {
    if (orcamentosSelecionados.length < 2 || moedasMistas) return;
    const ok = await confirm({
      title: `Mesclar ${orcamentosSelecionados.length} orçamentos`,
      message:
        `Será criado um novo orçamento em rascunho com os itens de todos, usando o cliente, as opções de pagamento e o texto de "${orcamentosSelecionados[0].nomeCliente}" (o primeiro selecionado). ` +
        'Os originais ficam como histórico e o link deles é revogado.',
      confirmText: 'Mesclar'
    });
    if (!ok) return;
    try {
      setMesclando(true);
      const res = await axios.post('/orcamento/mesclar', { ids: selecionados });
      notifications.success.settingsUpdated();
      setSelecionados([]);
      fetchOrcamentos();
      openEdit(res.data.orcamento);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao mesclar');
    } finally {
      setMesclando(false);
    }
  };

  const openSendLink = (id: string) => {
    setLinkOrcamentoId(id);
    setExpiresInDays('30');
    setViewUrlPreview('');
    setShowLinkModal(true);
  };

  const openViewLink = (o: OrcamentoData) => {
    setLinkOrcamentoId(o._id || null);
    setViewUrlPreview(o.viewUrl || '');
    setShowLinkModal(true);
  };

  const confirmSendLink = async () => {
    if (!linkOrcamentoId) return;
    const dias = Number(expiresInDays);
    if (!Number.isInteger(dias) || dias < 1 || dias > 90) {
      notifications.error.settingsUpdate('Informe uma validade entre 1 e 90 dias.');
      return;
    }
    try {
      setSendingLink(true);
      const res = await axios.post(`/orcamento/${linkOrcamentoId}/send-link`, { expiresInDays: dias });
      setViewUrlPreview(res.data.viewUrl);
      notifications.success.settingsUpdated();
      fetchOrcamentos();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao gerar link');
    } finally {
      setSendingLink(false);
    }
  };

  const { copy, isCopied } = useCopyToClipboard();
  const linkCopied = isCopied();
  const copyViewUrl = () => {
    if (viewUrlPreview) copy(viewUrlPreview);
  };

  const revokeLink = async () => {
    if (!linkOrcamentoId) return;
    if (!(await confirm({ title: 'Revogar link', message: 'O cliente não poderá mais acessar este orçamento.', confirmText: 'Revogar', variant: 'danger' }))) return;
    try {
      await axios.post(`/orcamento/${linkOrcamentoId}/revoke-link`);
      notifications.success.settingsUpdated();
      setShowLinkModal(false);
      fetchOrcamentos();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao revogar link');
    }
  };

  const postMarcarAceito = async (id: string, opcaoId?: string) => {
    try {
      setMarcandoAceito(true);
      await axios.post(`/orcamento/${id}/marcar-aceito`, opcaoId ? { opcaoId } : {});
      notifications.success.settingsUpdated();
      setAceiteOrcamento(null);
      fetchOrcamentos();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao marcar como aceito');
    } finally {
      setMarcandoAceito(false);
    }
  };

  const marcarAceito = async (o: OrcamentoData) => {
    if (!o._id) return;
    // Com opções de pagamento, o admin escolhe a forma pelo cliente num modal
    if (o.opcoesCalculadas && o.opcoesCalculadas.length > 0) {
      setAceiteOrcamento(o);
      setAceiteOpcaoId(o.opcoesCalculadas[0].id);
      return;
    }
    if (!(await confirm({ title: 'Marcar como aceito', message: 'O orçamento será marcado como aceito manualmente.', confirmText: 'Marcar como aceito' }))) return;
    postMarcarAceito(o._id);
  };

  const openInterno = (o: OrcamentoData) => {
    setInternoOrcamento(o);
    setInternoForm({
      origemCliente: o.origemCliente || '',
      origemDetalhe: o.origemDetalhe || '',
      notasInternas: o.notasInternas || ''
    });
  };

  const salvarInterno = async () => {
    if (!internoOrcamento?._id) return;
    try {
      setSalvandoInterno(true);
      const res = await axios.patch(`/orcamento/${internoOrcamento._id}/interno`, internoForm);
      const { origemCliente, origemDetalhe, notasInternas } = res.data;
      setOrcamentos((prev) =>
        prev.map((o) => (o._id === internoOrcamento._id ? { ...o, origemCliente, origemDetalhe, notasInternas } : o))
      );
      notifications.success.settingsUpdated();
      setInternoOrcamento(null);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao salvar notas internas');
    } finally {
      setSalvandoInterno(false);
    }
  };

  const converterEmContrato = async (id: string) => {
    if (!(await confirm({ title: 'Converter orçamento', message: 'Uma venda e as parcelas serão criadas no CRM.', confirmText: 'Converter' }))) return;
    try {
      setConverting(id);
      const res = await axios.post(`/orcamento/${id}/converter`);
      notifications.success.settingsUpdated();
      navigate('/admin/contrato', { state: { prefillContrato: res.data.prefillContrato } });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao converter orçamento');
    } finally {
      setConverting(null);
    }
  };

  // Orçamento já convertido (ex.: o admin cancelou o formulário do contrato): o /converter é
  // idempotente e não cria venda/parcelas de novo — só devolve o prefill e o contrato já salvo, se houver.
  const refazerContrato = async (id: string) => {
    try {
      setConverting(id);
      const res = await axios.post(`/orcamento/${id}/converter`);
      if (res.data.contratoId) {
        navigate('/admin/contrato', { state: { editContratoId: res.data.contratoId } });
      } else {
        navigate('/admin/contrato', { state: { prefillContrato: res.data.prefillContrato } });
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao abrir o contrato do orçamento');
    } finally {
      setConverting(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Breadcrumbs />
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Orçamento</h1>
          <p className="text-gray-600 mt-1 text-sm">
            Monte um orçamento com itens de serviço e forma de pagamento, envie um link para o cliente aceitar e converta em contrato.
          </p>
        </div>
        {!showForm && (
          <Button onClick={openCreate}>
            <Plus className="w-4 h-4" />
            Novo orçamento
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
            <h2 className="text-xl font-semibold">{editingId ? 'Editar orçamento' : 'Novo orçamento'}</h2>
            <IconButton action="close" onClick={() => setShowForm(false)} />
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Dados do cliente</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    required
                    value={formData.nomeCliente}
                    onChange={(e) => setFormData((prev) => ({ ...prev, nomeCliente: e.target.value }))}
                    className="field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.emailCliente || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, emailCliente: e.target.value }))}
                    className="field"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Opcional — se preenchido, o link é enviado por e-mail automaticamente; senão, copie e envie manualmente.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input
                    value={formData.telefoneCliente || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, telefoneCliente: e.target.value }))}
                    className="field"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg border border-dashed border-amber-300 bg-amber-50/60">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Lock className="w-4 h-4 text-amber-600" /> Uso interno
              </h3>
              <p className="text-xs text-gray-600 mt-1 mb-3">
                Visível só para a equipe — não aparece no link nem nos e-mails do cliente. Ao converter, vai para a origem e
                as observações do cliente no CRM.
              </p>
              <CamposInternos
                value={{
                  origemCliente: formData.origemCliente || '',
                  origemDetalhe: formData.origemDetalhe || '',
                  notasInternas: formData.notasInternas || ''
                }}
                onChange={(v) => setFormData((prev) => ({ ...prev, ...v }))}
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 border rounded-lg bg-gray-50">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Moeda do orçamento</label>
                <Tabs
                  variant="segmented"
                  ariaLabel="Moeda"
                  value={moedaForm}
                  onChange={(m) => handleMoedaChange(m)}
                  items={(['EUR', 'BRL'] as Moeda[]).map((m) => ({ id: m, label: m === 'EUR' ? '€ Euro' : 'R$ Real', disabled: carregandoCambio }))}
                />
              </div>
              {moedaForm === 'BRL' && (
                <div className="text-sm text-gray-600 sm:ml-2">
                  {formData.cambio ? (
                    <p className={formData.cambio.desatualizada ? 'text-amber-700' : ''}>
                      {labelCambio(formData.cambio)}
                      {formData.cambio.desatualizada && ' (fonte indisponível — última cotação conhecida)'}
                    </p>
                  ) : (
                    <p>Cotação ainda não carregada.</p>
                  )}
                  <button
                    type="button"
                    onClick={atualizarCambio}
                    disabled={carregandoCambio}
                    className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${carregandoCambio ? 'animate-spin' : ''}`} /> Atualizar cotação
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    Os serviços entram convertidos do valor em €; você pode ajustar cada valor. O cliente vê tudo em R$ e a
                    cotação fica gravada no orçamento.
                  </p>
                </div>
              )}
            </div>

            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <h3 className="font-semibold text-gray-900">Itens do orçamento</h3>
                <div className="flex flex-wrap items-center gap-3">
                  {servicos.length > 0 && (
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        list="orcamento-servicos-datalist"
                        value={servicoQuery}
                        onChange={(e) => {
                          const val = e.target.value;
                          const match = servicos.find((s) => servicoOptionLabel(s, taxaBrl) === val);
                          if (match) {
                            addItemFromServico(match._id);
                            setServicoQuery('');
                          } else {
                            setServicoQuery(val);
                          }
                        }}
                        placeholder="Buscar serviço para adicionar..."
                        className="field pl-8 pr-3 w-64"
                      />
                      <datalist id="orcamento-servicos-datalist">
                        {servicos.map((s) => (
                          <option key={s._id} value={servicoOptionLabel(s, taxaBrl)} />
                        ))}
                      </datalist>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={addItem}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-800"
                  >
                    <Plus className="w-4 h-4" /> Adicionar item manual
                  </button>
                  <Link
                    to="/admin/servicos"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800"
                  >
                    <Settings className="w-4 h-4" /> Gerenciar serviços
                  </Link>
                </div>
              </div>
              <div className="space-y-3">
                {formData.itens.map((item, index) => (
                  <div key={index} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_100px_auto] gap-3 items-start">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Descrição *</label>
                      <input
                        required
                        value={item.descricao}
                        onChange={(e) => handleItemChange(index, 'descricao', e.target.value)}
                        className="field"
                      />
                      <label className="mt-1 inline-flex items-center gap-2 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={!!item.abatidoAoFecharOutros}
                          onChange={(e) => handleItemAbatidoChange(index, e.target.checked)}
                        />
                        Abater do total se o cliente fechar outros serviços
                      </label>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Valor ({simboloForm}) *</label>
                      <CurrencyInput
                        moeda={moedaForm}
                        value={item.valor}
                        onChange={(v) => handleItemChange(index, 'valor', String(v))}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Qtd.</label>
                      <input
                        type="number"
                        min={1}
                        value={item.quantidade || ''}
                        placeholder="1"
                        onChange={(e) => handleItemChange(index, 'quantidade', e.target.value)}
                        onBlur={() => {
                          if (!item.quantidade) handleItemChange(index, 'quantidade', '1');
                        }}
                        className="field"
                      />
                    </div>
                    <IconButton action="delete" label="Remover item" onClick={() => removeItem(index)} disabled={formData.itens.length <= 1} className="mt-6 inline-flex disabled:opacity-30" />
                  </div>
                ))}
              </div>
              {(() => {
                const totais = calcularTotais(formData.itens, formData.abatimentoManual);
                return (
                  <div className="text-right mt-3 space-y-0.5">
                    {totais.abatimento > 0 && (
                      <>
                        <p className="text-sm text-gray-600">Subtotal: {fmt(totais.subtotal)}</p>
                        <p className="text-sm text-green-700">Abatimento: −{fmt(totais.abatimento)}</p>
                      </>
                    )}
                    <p className="font-semibold text-gray-900">Total: {fmt(totais.total)}</p>
                    {taxaBrl && totais.total > 0 && (
                      <p className="text-xs text-gray-500">≈ {formatMoeda(round2(totais.total / taxaBrl), 'EUR')} (uso interno)</p>
                    )}
                  </div>
                );
              })()}
            </div>

            <div>
              <h3 className="font-semibold text-gray-900">Opções de pagamento exibidas ao cliente *</h3>
              <p className="text-xs text-gray-500 mt-1 mb-3">
                O cliente vê todas as opções habilitadas com o valor final calculado e escolhe uma ao aceitar o orçamento.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3 p-3 mb-3 border rounded-lg bg-gray-50">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Abatimento manual ({simboloForm})</label>
                  <CurrencyInput
                    moeda={moedaForm}
                    value={formData.abatimentoManual?.valor || 0}
                    onChange={(v) =>
                      setFormData((prev) => ({
                        ...prev,
                        abatimentoManual: { descricao: prev.abatimentoManual?.descricao || '', valor: v }
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Descrição (opcional, visível ao cliente)</label>
                  <input
                    value={formData.abatimentoManual?.descricao || ''}
                    placeholder="Ex.: Análise Personalizada já paga"
                    maxLength={200}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        abatimentoManual: { valor: prev.abatimentoManual?.valor || 0, descricao: e.target.value }
                      }))
                    }
                    className="field"
                  />
                </div>
                <p className="sm:col-span-2 text-xs text-gray-500">
                  Descontado do total antes de calcular as opções abaixo. Total com abatimento:{' '}
                  <strong className="text-gray-900">{fmt(computeTotal(formData.itens, formData.abatimentoManual))}</strong>
                </p>
              </div>
              <div className="space-y-3">
                <label className="flex flex-wrap items-center gap-3 p-3 border rounded-lg">
                  <input
                    type="checkbox"
                    checked={opcoes.avista.habilitado}
                    onChange={(e) => updateOpcoes((o) => ({ ...o, avista: { habilitado: e.target.checked } }))}
                  />
                  <span className="font-medium text-gray-900">À vista</span>
                  <span className="ml-auto text-sm text-gray-600">{fmt(computeTotal(formData.itens, formData.abatimentoManual))}</span>
                </label>

                <div className="flex flex-wrap items-center gap-3 p-3 border rounded-lg">
                  <input
                    type="checkbox"
                    id="opcao-avista-desconto"
                    checked={opcoes.avistaDesconto.habilitado}
                    onChange={(e) =>
                      updateOpcoes((o) => ({ ...o, avistaDesconto: { ...o.avistaDesconto, habilitado: e.target.checked } }))
                    }
                  />
                  <label htmlFor="opcao-avista-desconto" className="font-medium text-gray-900">
                    À vista com desconto
                  </label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      value={opcoes.avistaDesconto.percentual || ''}
                      placeholder="0"
                      disabled={!opcoes.avistaDesconto.habilitado}
                      onChange={(e) =>
                        updateOpcoes((o) => ({
                          ...o,
                          avistaDesconto: { ...o.avistaDesconto, percentual: Number(e.target.value) || 0 }
                        }))
                      }
                      className="field w-24 disabled:bg-gray-100"
                    />
                    <span className="text-sm text-gray-600">%</span>
                  </div>
                  {opcoes.avistaDesconto.habilitado && (() => {
                    const opc = calcularOpcoes(computeTotal(formData.itens, formData.abatimentoManual), opcoes).find((x) => x.id === 'avista_desconto');
                    return opc ? (
                      <span className="ml-auto text-sm text-gray-600">
                        {fmt(opc.valorFinal)}{' '}
                        <span className="text-green-700">(−{fmt(opc.valorBase - opc.valorFinal)})</span>
                      </span>
                    ) : null;
                  })()}
                </div>

                <div className="p-3 border rounded-lg">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-gray-900">Parcelado com juros</span>
                    <button
                      type="button"
                      onClick={addParcelado}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-800"
                    >
                      <Plus className="w-4 h-4" /> Adicionar parcelamento
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Juros em % sobre o valor total, dividido igualmente entre as parcelas (0% = sem juros).
                  </p>
                  {opcoes.parcelados.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {opcoes.parcelados.map((p, index) => {
                        const opc = calcularOpcoes(computeTotal(formData.itens, formData.abatimentoManual), {
                          parcelados: [p]
                        })[0];
                        return (
                          <div key={index} className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min={MIN_PARCELAS}
                                max={MAX_PARCELAS}
                                value={p.parcelas || ''}
                                onChange={(e) => updateParcelado(index, 'parcelas', e.target.value)}
                                className="field w-20"
                                aria-label="Número de parcelas"
                              />
                              <span className="text-sm text-gray-600">x</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min={0}
                                max={100}
                                step={0.01}
                                value={p.juros || ''}
                                placeholder="0"
                                onChange={(e) => updateParcelado(index, 'juros', e.target.value)}
                                className="field w-24"
                                aria-label="Juros (%)"
                              />
                              <span className="text-sm text-gray-600">% juros</span>
                            </div>
                            <span className="text-sm text-gray-600">
                              {opc
                                ? `${opc.parcelas}x de ${fmt(opc.valorParcela)} · total ${fmt(opc.valorFinal)}`
                                : `Informe de ${MIN_PARCELAS} a ${MAX_PARCELAS} parcelas`}
                            </span>
                            <IconButton action="delete" label="Remover parcelamento" onClick={() => removeParcelado(index)} className="ml-auto inline-flex" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Texto do orçamento (pt/en/es)
              </label>
              <I18nTripleFields mode="orcamento" value={i18n} onChange={setI18n} />
            </div>

            <div className="form-actions">
              <Button variant="ghost" size="sm"
                type="button"
                onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}>
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Salvar</span>
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <>
          <div className="mb-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="field pl-10 pr-4"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as OrcamentoStatus | '')}
              className="field field--select sm:w-56 sm:shrink-0"
              aria-label="Filtrar por status"
            >
              <option value="">Todos os status</option>
              {(Object.keys(STATUS_LABEL) as OrcamentoStatus[]).map((st) => (
                <option key={st} value={st}>
                  {STATUS_LABEL[st].label}
                </option>
              ))}
            </select>
            <select
              value={origemFilter}
              onChange={(e) => setOrigemFilter(e.target.value as OrigemCliente | '')}
              className="field field--select sm:w-56 sm:shrink-0"
              aria-label="Filtrar por origem do cliente"
            >
              <option value="">Todas as origens</option>
              {(Object.keys(ORIGEM_CLIENTE_LABEL) as OrigemCliente[]).map((k) => (
                <option key={k} value={k}>
                  {ORIGEM_CLIENTE_LABEL[k]}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
            </div>
          ) : orcamentos.length === 0 ? (
            <p className="text-gray-500 text-center py-12">Nenhum orçamento. Clique em &quot;Novo orçamento&quot;.</p>
          ) : (
            <div className="space-y-4">
              {selecionados.length > 0 && (
                <div className="sticky top-2 z-20 flex flex-wrap items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 shadow-sm">
                  <span className="text-sm font-medium text-gray-800">
                    {selecionados.length} selecionado{selecionados.length > 1 ? 's' : ''}
                  </span>
                  {selecionados.length < 2 && (
                    <span className="text-xs text-gray-600">Selecione mais um para mesclar</span>
                  )}
                  {moedasMistas && (
                    <span className="text-xs text-red-700">Só é possível mesclar orçamentos na mesma moeda</span>
                  )}
                  <div className="ml-auto flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSelecionados([])}
                      className="px-3 py-1.5 text-sm text-gray-700 hover:bg-white rounded-md"
                    >
                      Limpar
                    </button>
                    <Button size="sm"
                      type="button"
                      onClick={handleMesclar}
                      disabled={selecionados.length < 2 || moedasMistas || mesclando}>
                      <Merge className="w-4 h-4" />
                      {mesclando ? 'Mesclando...' : 'Mesclar em um orçamento'}
                    </Button>
                  </div>
                </div>
              )}
              {orcamentos.map((o) => {
                const status = o.status || 'draft';
                const statusInfo = o.mescladoEm
                  ? { label: 'Mesclado', className: 'bg-gray-100 text-gray-600' }
                  : STATUS_LABEL[status];
                const selecionado = !!o._id && selecionados.includes(o._id);
                return (
                  <div
                    key={o._id}
                    className={`bg-white rounded-lg shadow p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 ${
                      selecionado ? 'ring-2 ring-primary-400' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {podeMesclar(o) && (
                        <input
                          type="checkbox"
                          checked={selecionado}
                          onChange={() => o._id && toggleSelecionado(o._id)}
                          className="check-input mt-1"
                          aria-label={`Selecionar orçamento de ${o.nomeCliente} para mesclar`}
                          title="Selecionar para mesclar"
                        />
                      )}
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {temInfoInterna(o) ? (
                          <div className="relative group">
                            <button
                              type="button"
                              onClick={() => openInterno(o)}
                              className="flex items-center gap-1.5 font-semibold text-gray-900 underline decoration-dotted decoration-amber-400 underline-offset-4 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded"
                              aria-label={`${o.nomeCliente} — ver ou editar notas internas`}
                            >
                              {o.nomeCliente}
                              <StickyNote className="w-4 h-4 text-amber-500" aria-hidden="true" />
                            </button>
                            <div
                              role="tooltip"
                              className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-opacity duration-150 absolute left-0 top-full z-30 mt-2 w-80 max-w-[calc(100vw-3rem)] rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-gray-800 shadow-lg"
                            >
                              <p className="flex items-center gap-1 font-medium text-amber-800 mb-1">
                                <Lock className="w-3 h-3" /> Uso interno
                              </p>
                              {labelOrigem(o) && (
                                <p>
                                  <span className="font-medium">Origem:</span> {labelOrigem(o)}
                                </p>
                              )}
                              {o.notasInternas && (
                                <p className="mt-1 whitespace-pre-line max-h-48 overflow-y-auto">{o.notasInternas}</p>
                              )}
                              <p className="mt-2 text-[11px] text-amber-700">Clique no nome para editar</p>
                            </div>
                          </div>
                        ) : (
                          <p className="font-semibold text-gray-900">{o.nomeCliente}</p>
                        )}
                        <span className={`px-2 py-0.5 text-xs rounded-full ${statusInfo.className}`}>
                          {statusInfo.label}
                        </span>
                        {o.origemCliente && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                            {ORIGEM_CLIENTE_LABEL[o.origemCliente]}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{o.emailCliente || 'Sem e-mail cadastrado'}</p>
                      <p className="text-xs text-gray-500">
                        Total: {formatMoeda(o.total ?? computeTotal(o.itens), o.moeda)} ·{' '}
                        {o.opcaoEscolhida
                          ? `Escolhido: ${labelOpcaoAdmin(o.opcaoEscolhida, o.moeda)}`
                          : o.opcoesCalculadas && o.opcoesCalculadas.length > 0
                            ? `${o.opcoesCalculadas.length} opç${o.opcoesCalculadas.length > 1 ? 'ões' : 'ão'} de pagamento`
                            : labelFormaPagamento(o.formaPagamento)}
                      </p>
                      {o.createdAt && (
                        <p className="text-xs text-gray-500">{new Date(o.createdAt).toLocaleDateString('pt-PT')}</p>
                      )}
                      {o.mescladoDe && o.mescladoDe.length > 0 && (
                        <p className="text-xs text-primary-700">Mesclado de {o.mescladoDe.length} orçamentos</p>
                      )}
                      {o.mescladoEm && (
                        <p className="text-xs text-gray-500">Faz parte de um orçamento mesclado — o link deste foi revogado</p>
                      )}
                      {o.duplicadoDe && <p className="text-xs text-gray-500">Cópia de outro orçamento</p>}
                      {o.recusa && (
                        <div className="mt-2 text-xs rounded-md bg-red-50 border border-red-100 px-3 py-2 text-red-800">
                          <p>
                            <span className="font-medium">
                              {status === 'declined' ? 'Motivo da recusa' : 'Recusou anteriormente'}:
                            </span>{' '}
                            {MOTIVO_RECUSA_LABEL[o.recusa.motivo] || o.recusa.motivo}
                            {o.recusa.recusadoAt && (
                              <span className="text-red-600"> · {new Date(o.recusa.recusadoAt).toLocaleDateString('pt-PT')}</span>
                            )}
                          </p>
                          {o.recusa.comentario && (
                            <p className="mt-1 italic whitespace-pre-line">&ldquo;{o.recusa.comentario}&rdquo;</p>
                          )}
                        </div>
                      )}
                    </div>
                    </div>
                    <div className="row-actions md:flex-wrap">
                      <ActionButton
                        icon={StickyNote}
                        label="Notas"
                        variant="warning"
                        onClick={() => openInterno(o)}
                      />
                      {!['accepted', 'converted'].includes(status) && !o.mescladoEm && (
                        <ActionButton
                          action="edit"
                          onClick={() => openEdit(o)}
                        />
                      )}
                      {status === 'sent' || status === 'expired' ? (
                        <ActionButton
                          icon={LinkIcon}
                          label="Ver link"
                          variant="warning"
                          onClick={() => openViewLink(o)}
                        />
                      ) : status === 'draft' || status === 'declined' ? (
                        <ActionButton
                          action="send"
                          label="Gerar link"
                          onClick={() => o._id && openSendLink(o._id)}
                        />
                      ) : null}
                      <ActionButton
                        action="duplicate"
                        onClick={() => handleDuplicar(o)}
                        disabled={duplicando === o._id}
                      />
                      {!['accepted', 'converted'].includes(status) && !o.mescladoEm && (
                        <ActionButton
                          action="approve"
                          label="Aceito"
                          onClick={() => marcarAceito(o)}
                        />
                      )}
                      {status === 'accepted' && (
                        <ActionButton
                          icon={ArrowRightCircle}
                          label="Contrato"
                          variant="primary"
                          onClick={() => o._id && converterEmContrato(o._id)}
                          disabled={converting === o._id}
                        />
                      )}
                      {status === 'converted' && (
                        <ActionButton
                          icon={RotateCcw}
                          label="Refazer"
                          variant="primary"
                          onClick={() => o._id && refazerContrato(o._id)}
                          disabled={converting === o._id}
                        />
                      )}
                      {status !== 'converted' && (
                        <ActionButton
                          action="delete"
                          onClick={() => o._id && handleDelete(o._id)}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {internoOrcamento && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg p-6">
            <div className="flex justify-between items-center mb-1">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <StickyNote className="w-5 h-5 text-amber-500" />
                Notas internas
              </h3>
              <IconButton action="close" onClick={() => setInternoOrcamento(null)} />
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {internoOrcamento.nomeCliente} · visível só para a equipe
            </p>
            <CamposInternos value={internoForm} onChange={setInternoForm} />
            <div className="form-actions mt-4">
              <Button variant="ghost" size="sm"
                onClick={() => setInternoOrcamento(null)}>
                Cancelar
              </Button>
              <Button
                onClick={salvarInterno}
                disabled={salvandoInterno}>
                {salvandoInterno && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                Salvar
              </Button>
            </div>
          </div>
        </div>
      )}

      {aceiteOrcamento && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Marcar como aceito
              </h3>
              <IconButton action="close" onClick={() => setAceiteOrcamento(null)} />
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Qual forma de pagamento {aceiteOrcamento.nomeCliente} escolheu?
            </p>
            <div className="space-y-2 mb-6">
              {(aceiteOrcamento.opcoesCalculadas || []).map((opc) => (
                <label
                  key={opc.id}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                    aceiteOpcaoId === opc.id ? 'border-primary-500 bg-primary-50' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="aceite-opcao"
                    checked={aceiteOpcaoId === opc.id}
                    onChange={() => setAceiteOpcaoId(opc.id)}
                  />
                  <span className="flex-1 text-sm text-gray-900">{labelOpcaoAdmin(opc, aceiteOrcamento?.moeda)}</span>
                  <span className="text-sm font-medium text-gray-900">{formatMoeda(opc.valorFinal, aceiteOrcamento?.moeda)}</span>
                </label>
              ))}
            </div>
            <div className="form-actions">
              <Button variant="ghost" size="sm"
                onClick={() => setAceiteOrcamento(null)}>
                Cancelar
              </Button>
              <Button
                onClick={() => aceiteOrcamento._id && postMarcarAceito(aceiteOrcamento._id, aceiteOpcaoId)}
                disabled={marcandoAceito || !aceiteOpcaoId}>
                {marcandoAceito && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                Confirmar aceite
              </Button>
            </div>
          </div>
        </div>
      )}

      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Send className="w-5 h-5" />
                {viewUrlPreview ? 'Link do orçamento' : 'Gerar link para o cliente'}
              </h3>
              <IconButton action="close" onClick={() => setShowLinkModal(false)} />
            </div>

            {!viewUrlPreview ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  {orcamentos.find((o) => o._id === linkOrcamentoId)?.emailCliente
                    ? 'Um link será enviado por e-mail ao cliente para revisar e aceitar o orçamento, sem necessidade de conta.'
                    : 'Este orçamento não tem e-mail cadastrado — o link será gerado para você copiar e enviar manualmente (WhatsApp, etc.).'}
                </p>
                {orcamentos.find((o) => o._id === linkOrcamentoId)?.viewUrl && (
                  <p className="text-sm text-gray-600 -mt-2 mb-4">
                    O link enviado anteriormente continua o mesmo, só com a nova validade.
                  </p>
                )}
                <label className="block mb-4">
                  <span className="block text-sm font-medium text-gray-700 mb-1">Validade do link (dias)</span>
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={expiresInDays}
                    placeholder="30"
                    onChange={(e) => setExpiresInDays(e.target.value)}
                    className="field"
                  />
                </label>
                <div className="flex flex-wrap gap-2 -mt-2 mb-4">
                  {[7, 15, 30, 60, 90].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setExpiresInDays(String(d))}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors duration-200 ${
                        Number(expiresInDays) === d
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {d} dias
                    </button>
                  ))}
                </div>
                <div className="form-actions">
                  <Button variant="ghost" size="sm"
                    onClick={() => setShowLinkModal(false)}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={confirmSendLink}
                    disabled={sendingLink}>
                    {sendingLink && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                    Enviar
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-2">Link do orçamento para o cliente:</p>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    readOnly
                    value={viewUrlPreview}
                    className="field flex-1 bg-gray-50"
                    onFocus={(e) => e.target.select()}
                  />
                  <IconButton action="copyLink" onClick={copyViewUrl} />
                </div>
                {linkCopied && <p className="text-xs text-green-600 mb-4">Link copiado!</p>}
                <div className="form-actions">
                  <Button variant="ghost" size="sm" className="text-red-700 hover:bg-red-50 border-red-200"
                    onClick={revokeLink}>
                    <Ban className="w-4 h-4" /> Revogar link
                  </Button>
                  <Button onClick={() => setShowLinkModal(false)}>
                    Fechar
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default AdminOrcamento;
